// Status grouping shared by the support portal and the widget requests view, so
// both surfaces show the same sections in the same order.

import type { Translator } from "./roadmap-status"
import { getStatusFallbackLabel, getStatusLabel } from "./roadmap-status"

export const REQUEST_STATUS_ORDER: Record<string, number> = {
	started: 0,
	unstarted: 1,
	planned: 2,
	triage: 3,
	backlog: 4,
	paused: 5,
	inbox: 6,
	completed: 7,
}

// Groups are keyed by Linear status type. `unstarted` is Linear's "Todo" state, so
// it reads as "To do" here, the same label the feature request page gives it.
const REQUEST_STATUS_LABEL_KEYS: Record<string, string> = {
	started: "inProgress",
	unstarted: "todo",
	planned: "planned",
	triage: "triage",
	backlog: "backlog",
	paused: "paused",
	completed: "completed",
}

export const REQUEST_STATUS_LABELS: Record<string, string> = {
	...Object.fromEntries(
		Object.keys(REQUEST_STATUS_ORDER).map((status) => [
			status,
			getStatusFallbackLabel(REQUEST_STATUS_LABEL_KEYS[status] ?? status),
		]),
	),
	inbox: "Inbox",
}

// Terminal Linear statuses we don't surface as their own group.
const HIDDEN_STATUSES = new Set([
	"duplicate",
	"canceled",
	"cancelled",
	"closed",
])

export const isHiddenRequestStatus = (key: string): boolean =>
	HIDDEN_STATUSES.has(key.trim().toLowerCase())

export interface RequestStatusInput {
	type: "issue" | "project" | "feedback"
	category: string
	item: {
		status?: string | null
		statusName?: string | null
		state?: string | null
	}
}

export const getRequestStatusTypeKey = (
	request: RequestStatusInput,
): string => {
	if (request.type === "issue") return request.item.status ?? "unstarted"
	if (request.type === "project") return request.item.state ?? "backlog"
	return request.category === "completed" ? "completed" : "inbox"
}

export const getRequestStatusKey = (request: RequestStatusInput): string => {
	if (request.type === "issue") {
		const name = request.item.statusName?.trim()
		if (name) return name
	}
	return getRequestStatusTypeKey(request)
}

export type RequestIdentifierInput =
	| { type: "issue"; item: { identifier?: string | null } }
	| { type: "project" | "feedback"; item: unknown }

// Only Linear issues carry an identifier; projects and standalone threads have none.
export const getRequestIssueIdentifier = (
	request: RequestIdentifierInput,
): string | null => {
	if (request.type !== "issue") return null
	return request.item.identifier?.trim() || null
}

export interface RequestDateInput extends RequestStatusInput {
	createdAt?: string | Date | null
	resolvedAt?: string | Date | null
	item: RequestStatusInput["item"] & { createdAt?: string | Date | null }
}

// A completed request is audited by when it closed, so that row shows resolvedAt.
export const getRequestDisplayDate = (
	request: RequestDateInput,
): string | Date | null => {
	if (getRequestStatusKey(request) === "completed" && request.resolvedAt) {
		return request.resolvedAt
	}
	return request.createdAt ?? request.item.createdAt ?? null
}

export const getRequestStatusLabel = (
	status: string,
	t?: Translator,
): string => {
	const key = REQUEST_STATUS_LABEL_KEYS[status]
	if (key) return getStatusLabel(key, t)
	return REQUEST_STATUS_LABELS[status] ?? getStatusLabel(status, t)
}

export interface RequestStatusGroup<T> {
	status: string
	// Linear status type behind the group, for the icon and the order.
	statusType: string
	requests: T[]
}

export function groupRequestsByStatus<T extends RequestStatusInput>(
	requests: T[],
	sortWithinGroup?: (a: T, b: T) => number,
): RequestStatusGroup<T>[] {
	const groups: Record<string, T[]> = {}
	const orderKeys: Record<string, string> = {}
	for (const request of requests) {
		const status = getRequestStatusKey(request)
		const typeKey = getRequestStatusTypeKey(request)
		if (isHiddenRequestStatus(status) || isHiddenRequestStatus(typeKey))
			continue
		groups[status] = [...(groups[status] ?? []), request]
		orderKeys[status] ??= typeKey
	}
	return Object.entries(groups)
		.map(([status, reqs]) => ({
			status,
			statusType: orderKeys[status] ?? status,
			requests: sortWithinGroup ? [...reqs].sort(sortWithinGroup) : reqs,
		}))
		.sort(
			(a, b) =>
				(REQUEST_STATUS_ORDER[a.statusType] ?? 99) -
				(REQUEST_STATUS_ORDER[b.statusType] ?? 99),
		)
}
